import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, inr, todayISO } from "@/lib/api";
import { BOOKING_STATUSES, PAYMENT_MODES } from "@/lib/constants";
import { marginCls } from "./common";

const blank = { customer_name: "", phone: "", email: "", package_id: "none", package_name: "", travel_date: "", guests: 2,
  cost_per_person: 0, price_per_person: 0, advance_paid: 0, advance_mode: "UPI", advance_date: todayISO(), status: "confirmed", notes: "", enquiry_id: null };

export const BookingDialog = ({ open, onOpenChange, booking, enquiry, onSaved }) => {
  const [pkgs, setPkgs] = useState([]);
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open && !pkgs.length) api.get("/admin/packages").then((r) => setPkgs(r.data)); }, [open, pkgs.length]);
  useEffect(() => {
    if (!open) return;
    if (booking) setF({ ...blank, ...booking, package_id: booking.package_id || "none" });
    else if (enquiry) setF({ ...blank, customer_name: enquiry.name, phone: enquiry.phone, email: enquiry.email || "", travel_date: enquiry.travel_date,
      guests: enquiry.guests, package_id: enquiry.package_id || "none", package_name: enquiry.package_name, enquiry_id: enquiry.id });
    else setF(blank);
  }, [open, booking, enquiry]);
  useEffect(() => {
    if (!open || booking || f.package_id === "none" || !pkgs.length) return;
    const p = pkgs.find((x) => x.id === f.package_id);
    if (p) setF((s) => ({ ...s, package_name: p.name, cost_per_person: p.cost_price, price_per_person: p.selling_price }));
  }, [f.package_id, pkgs, open, booking]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const n = (v) => Number(v) || 0;
  const totalCost = n(f.cost_per_person) * n(f.guests);
  const totalPrice = n(f.price_per_person) * n(f.guests);
  const profit = totalPrice - totalCost;
  const margin = totalPrice ? (profit / totalPrice) * 100 : 0;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...f, guests: n(f.guests), cost_per_person: n(f.cost_per_person), price_per_person: n(f.price_per_person),
      advance_paid: n(f.advance_paid), package_id: f.package_id === "none" ? null : f.package_id };
    ["id", "payments", "booking_no", "total_cost", "total_price", "profit", "margin", "balance_due", "created_at", "updated_at"].forEach((k) => delete payload[k]);
    try {
      const { data } = booking ? await api.put(`/admin/bookings/${booking.id}`, payload) : await api.post("/admin/bookings", payload);
      toast.success(booking ? "Booking updated" : "Booking created");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const field = (k, label, props = {}) => (
    <div className="space-y-1.5"><Label>{label}</Label><Input value={f[k]} onChange={set(k)} data-testid={`booking-${k}-input`} {...props} /></div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader><DialogTitle>{booking ? `Edit booking ${booking.booking_no}` : "New booking"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          {field("customer_name", "Customer name *", { required: true, minLength: 2 })}
          {field("phone", "Phone *", { required: true, minLength: 7 })}
          {field("email", "Email", { type: "email" })}
          {field("travel_date", "Travel date *", { required: true, type: "date" })}
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Package</Label>
            <Select value={f.package_id} onValueChange={(v) => setF((s) => ({ ...s, package_id: v }))}>
              <SelectTrigger data-testid="booking-package-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="none">Custom package</SelectItem>
                {pkgs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {f.package_id === "none" && <div className="sm:col-span-2">{field("package_name", "Custom package name")}</div>}
          {field("guests", "Guests *", { type: "number", min: 1, required: true })}
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF((s) => ({ ...s, status: v }))}>
              <SelectTrigger data-testid="admin-booking-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{BOOKING_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("cost_per_person", "Cost per person (₹)", { type: "number", min: 0, step: "any" })}
          {field("price_per_person", "Selling price per person (₹)", { type: "number", min: 0, step: "any" })}
          {!booking && (<>
            {field("advance_paid", "Advance received (₹)", { type: "number", min: 0, step: "any" })}
            <div className="space-y-1.5">
              <Label>Advance payment mode</Label>
              <Select value={f.advance_mode} onValueChange={(v) => setF((s) => ({ ...s, advance_mode: v }))}>
                <SelectTrigger data-testid="booking-advance-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {field("advance_date", "Advance payment date", { type: "date" })}
          </>)}
          {booking && <p className="sm:col-span-2 text-xs text-slate-500" data-testid="booking-payments-hint">Payments received: {inr(booking.advance_paid)}. Add or remove payments using the Payments button on the Bookings list.</p>}
          <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-xl bg-mist p-4 text-sm" data-testid="booking-summary">
            <div><p className="text-xs text-slate-500">Total cost</p><p className="font-mono font-semibold">{inr(totalCost)}</p></div>
            <div><p className="text-xs text-slate-500">Total price</p><p className="font-mono font-semibold text-ocean">{inr(totalPrice)}</p></div>
            <div><p className="text-xs text-slate-500">Profit</p><p className={`font-mono font-semibold ${marginCls(margin)}`}>{inr(profit)} ({margin.toFixed(1)}%)</p></div>
            <div><p className="text-xs text-slate-500">Balance due</p><p className="font-mono font-semibold text-coral">{inr(Math.max(totalPrice - n(f.advance_paid), 0))}</p></div>
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Notes</Label><Textarea rows={3} value={f.notes} onChange={set("notes")} data-testid="booking-notes-input" /></div>
          <Button type="submit" disabled={saving} className="sm:col-span-2 bg-ocean hover:bg-navy h-11" data-testid="booking-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : booking ? "Save changes" : "Create booking"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
